import pino from 'pino';

const isBrowser = typeof window !== 'undefined';
const isClientBuild =
  typeof __TURFMASTER_CLIENT_BUILD__ !== 'undefined' && __TURFMASTER_CLIENT_BUILD__;
const useBrowserConfig = isBrowser || isClientBuild;

const COLOR = {
  GREEN: `\x1b[32m`,
  RED: `\x1b[31m`,
  WHITE: `\x1b[37m`,
  YELLOW: `\x1b[33m`,
  CYAN: `\x1b[36m`,
};

const LEVEL_COLORS: Record<string, string> = {
  FATAL: COLOR.RED,
  ERROR: COLOR.RED,
  WARN: COLOR.YELLOW,
  INFO: COLOR.GREEN,
  DEBUG: COLOR.GREEN,
  TRACE: COLOR.GREEN,
};

/** Safe timestamp for browser: avoids Invalid time value from Date#toISOString. */
function formatTimestamp(time: unknown): string {
  if (time == null) return '';
  try {
    const d = new Date(String(time));
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(11, 23);
  } catch {
    return '';
  }
}

/** Coerce to string without throwing (e.g. symbols or objects with broken toString). */
function safeString(value: unknown): string {
  try {
    return value == null ? '' : String(value);
  } catch {
    return '[?]';
  }
}

// Guard Node-only env access so this module is safe when evaluated in the browser (e.g. via shared bundles).
const nodeEnv =
  typeof process !== 'undefined' && process.env ? process.env : ({} as NodeJS.Process['env']);
const defaultLevel = 'info';
const level = useBrowserConfig
  ? nodeEnv.GATSBY_LOG_LEVEL || defaultLevel
  : nodeEnv.LOG_LEVEL || defaultLevel;

const logger = pino({
  level,
  browser: useBrowserConfig
    ? {
        write(logObj: unknown) {
          try {
            if (logObj == null || typeof logObj !== 'object') {
              console.log('[logger]', logObj);
              return;
            }
            const obj = logObj as Record<string, unknown>;
            const level = obj.level;
            const msg = obj.msg;
            const context = obj.context;
            const time = obj.time;
            const logParams = { ...obj };
            delete logParams.level;
            delete logParams.msg;
            delete logParams.context;
            delete logParams.time;

            const label = safeString(level).toUpperCase() || 'INFO';
            const labelColor = LEVEL_COLORS[label] ?? '';
            const reset = '\x1b[0m';
            const ts = formatTimestamp(time);
            const contextPart = context ? `${COLOR.CYAN}${safeString(context)} ${reset}` : '';

            console.log(
              `${ts} ${labelColor}${label} ${contextPart}${safeString(msg)}`,
              ...Object.values(logParams),
            );
          } catch (err) {
            console.error(
              '[logger] write failed:',
              err instanceof Error ? err.message : String(err),
              logObj,
            );
          }
        },
        formatters: {
          level: (label) => ({ level: label }),
        },
      }
    : undefined,
  ...(useBrowserConfig
    ? {}
    : {
        timestamp: pino.stdTimeFunctions.isoTime,
        formatters: {
          level: (label) => ({ level: label }),
        },
        base: {
          'service.name': nodeEnv.OTEL_SERVICE_NAME || nodeEnv.SERVICE_NAME || 'turfmaster-server',
          'service.version': nodeEnv.BUILD_VERSION || 'unknown',
          'deployment.environment': nodeEnv.NODE_ENV || 'development',
        },
        redact: {
          paths: [
            'req.headers.authorization',
            'req.headers.cookie',
            'password',
            'apiKey',
            'secret',
            'token',
          ],
          censor: '[REDACTED]',
        },
      }),
});

export const socketLogger = logger.child({ context: 'socket' });
export const cronLogger = logger.child({ context: 'cron' });
export const webhookLogger = logger.child({ context: 'webhook' });
export const gameLogger = logger.child({ context: 'game' });
export const paymentLogger = logger.child({ context: 'payment' });

export default logger;
